
import {Injectable, NgZone, Renderer2} from "@angular/core";
import {LoggerService} from "../logger/logger.service";

interface TooltipInstance {
    host: HTMLElement;
    tooltipEl: HTMLElement;
    placement: string;
    trigger: string;
    listeners: Array<() => void>;
}

@Injectable() export class TooltipService {

    public static readonly TOOLTIP_MODE_CLICK:string = "click";
    public static readonly TOOLTIP_MODE_HOVER:string = "hover";
    public static readonly TOOLTIP_MODE_MANUAL:string = "manual";

    public static readonly PLACEMENT_BOTTOM:string = "bottom";
    public static readonly PLACEMENT_TOP:string = "top";
    public static readonly PLACEMENT_LEFT:string = "left";
    public static readonly PLACEMENT_RIGHT:string = "right";

    private instances = new WeakMap<HTMLElement, TooltipInstance>();

    constructor(private zone:NgZone, protected logger:LoggerService) {}

    public destroyToolTip(element:any)
    {
        const host: HTMLElement | null = element && element.nativeElement ? element.nativeElement as HTMLElement : null;
        if (!host) { return; }
        const instance = this.instances.get(host);
        if (instance) {
            // remove listeners
            instance.listeners.forEach(unsub => {
                try { unsub(); } catch {}
            });
            // remove tooltip element
            if (instance.tooltipEl && instance.tooltipEl.parentNode) {
                instance.tooltipEl.parentNode.removeChild(instance.tooltipEl);
            }
            this.instances.delete(host);
        }
        // remove attributes
        host.removeAttribute('data-toggle');
        host.removeAttribute('data-placement');
        host.removeAttribute('title');
        host.removeAttribute('container');
    }

    public createToolTip(renderer:Renderer2, element:any, content:string, tooltipPlacement:string = TooltipService.PLACEMENT_BOTTOM, tooltipMode:string = "click", tooltipClass:string = "", alwaysOnTop:boolean = false)
    {
        // Remove existing tooltips on this element
        this.destroyToolTip(element);

        const host: HTMLElement | null = element && element.nativeElement ? element.nativeElement as HTMLElement : null;
        if (host) {
            this.zone.runOutsideAngular(() => {
                this.destroyToolTip(element);

                renderer.setAttribute(host, 'data-toggle', 'tooltip');
                renderer.setAttribute(host, 'data-placement', tooltipPlacement);
                renderer.setAttribute(host, 'title', content);
                renderer.setAttribute(host, 'container', 'form-component');

                // Create tooltip element
                const tooltip = document.createElement('div');
                tooltip.className = 'tooltip' + (alwaysOnTop ? ' top-most' : '');
                const arrow = document.createElement('div');
                arrow.className = 'tooltip-arrow' + (tooltipClass ? ' ' + tooltipClass : '');
                const inner = document.createElement('div');
                inner.className = 'tooltip-inner' + (tooltipClass ? ' ' + tooltipClass : '');
                // content allows HTML if desired similarly to previous config (html: true)
                inner.innerHTML = content || '';
                tooltip.appendChild(arrow);
                tooltip.appendChild(inner);
                tooltip.style.position = 'absolute';
                tooltip.style.display = 'none';
                tooltip.style.opacity = '1';
                tooltip.setAttribute('role','tooltip');
                document.body.appendChild(tooltip);

                const place = () => this.positionTooltip(host, tooltip, tooltipPlacement);
                const show = () => { tooltip.style.display = 'block'; place(); };
                const hide = () => { tooltip.style.display = 'none'; };

                const listeners: Array<() => void> = [];
                const add = (el: Element | Window | Document, ev: string, cb: any) => {
                    el.addEventListener(ev, cb as EventListener, { passive: true });
                    listeners.push(() => el.removeEventListener(ev, cb as EventListener));
                };

                if (tooltipMode === TooltipService.TOOLTIP_MODE_CLICK) {
                    add(host, 'click', (e: Event) => { e.stopPropagation(); show(); });
                    add(document, 'click', () => hide());
                } else if (tooltipMode === TooltipService.TOOLTIP_MODE_HOVER) {
                    add(host, 'mouseenter', show);
                    add(host, 'mouseleave', hide);
                    add(window, 'scroll', place);
                } // manual -> no automatic listeners

                document.addEventListener('click', () => {
                    this.hideAllTooltips();
                }, { once: true });

                // Save instance
                this.instances.set(host, { host, tooltipEl: tooltip, placement: tooltipPlacement, trigger: tooltipMode, listeners });
            });
        } else {
            this.logger.log('[TooltipService]' + 'create error - provided element is not of type ElementRef');
        }
    }

    private positionTooltip(host: HTMLElement, tooltip: HTMLElement, placement: string) {
        const rect = host.getBoundingClientRect();
        const tipRect = tooltip.getBoundingClientRect();
        const scrollY = window.scrollY || document.documentElement.scrollTop;
        const scrollX = window.scrollX || document.documentElement.scrollLeft;
        let top = 0, left = 0;
        switch (placement) {
            case TooltipService.PLACEMENT_TOP:
                top = rect.top + scrollY - tipRect.height - 8; // 8px offset
                left = rect.left + scrollX + (rect.width - tipRect.width) / 2;
                break;
            case TooltipService.PLACEMENT_LEFT:
                top = rect.top + scrollY + (rect.height - tipRect.height) / 2;
                left = rect.left + scrollX - tipRect.width - 8;
                break;
            case TooltipService.PLACEMENT_RIGHT:
                top = rect.top + scrollY + (rect.height - tipRect.height) / 2;
                left = rect.right + scrollX + 8;
                break;
            case TooltipService.PLACEMENT_BOTTOM:
            default:
                top = rect.bottom + scrollY + 8;
                left = rect.left + scrollX + (rect.width - tipRect.width) / 2;
                break;
        }
        tooltip.style.top = `${top}px`;
        tooltip.style.left = `${left}px`;
    }

    public hideAllTooltips():void
    {
        const nodes = document.querySelectorAll('.tooltip');
        nodes.forEach((n: Element) => {
            const el = n as HTMLElement;
            el.style.display = 'none';
        });
    }

    public showTooltip(element:any)
    {
        const host: HTMLElement | null = element && element.nativeElement ? element.nativeElement as HTMLElement : null;
        if (host) {
            const instance = this.instances.get(host);
            if (instance) {
                instance.tooltipEl.style.display = 'block';
                this.positionTooltip(host, instance.tooltipEl, instance.placement);
            } else {
                this.logger.log('[TooltipService]' + 'show error - tooltip not initialized for element');
            }
        } else {
            this.logger.log('[TooltipService]' + 'show error - provided element is not of type ElementRef');
        }
    }

    public createAndShowTooltip(renderer:Renderer2, element:any, content:string, tooltipPlacement:string = TooltipService.PLACEMENT_BOTTOM, tooltipMode:string = TooltipService.TOOLTIP_MODE_MANUAL, tooltipClass:string = "", alwaysOnTop:boolean = false)
    {
        this.createToolTip(renderer, element, content, tooltipPlacement, tooltipMode, tooltipClass, alwaysOnTop);
        this.showTooltip(element);
    }
}
